/**
 * The B2B failure vocabulary — pure, no Angular. `OrgErrorMapper` (application
 * layer) turns an `ApiError` into an {@link OrgError} using the tables here.
 *
 * Two things make org failures different from every other call in the app and
 * are the whole reason this file exists:
 *
 *  1. **§1.3 — the 403 message is ALWAYS clobbered.** `customResponse()`
 *     overwrites `message` for 403 unconditionally, so whatever the controller
 *     passed is gone by the time it reaches us. The real reason survives only in
 *     `data.reason`, and only where the controller sent one. So: never render a
 *     403's `message`. {@link orgMessageKeyFor} returns our own copy instead.
 *  2. **§1.2 — four different failure shapes.** `clientError()` envelopes,
 *     raw Laravel validation (no `status`, no `data`), and two handler shapes
 *     that DO carry a `code`. Everything below is written to tolerate all four.
 */

/**
 * Every stable `data.reason` the guide documents, across every endpoint.
 * Grouped by the endpoint that emits it; the union is flat because one shared
 * mapper reads them all.
 */
export type OrgReason =
  // ── POST /engagement/challenges/{id}/progress (§4.4) ──
  | 'not_yours'
  | 'not_self_report'
  | 'challenge_ended'
  | 'future_date'
  | 'outside_window'
  | 'too_old'
  | 'not_joined'
  | 'invalid_value'
  | 'above_max'
  // ── GET /counseling/options (§7.1) ──
  | 'proxy_stage'
  | 'no_slots'
  // ── POST /counseling/bookings (§7.2) ──
  | 'slot_not_found'
  | 'date_not_offered'
  | 'already_booked'
  | 'guardian_missing'
  | 'slot_full'
  | 'not_a_member'
  // ── POST /counseling/bookings/{id}/cancel (§7.4) ──
  | 'not_found'
  // ── POST /health/samples per-row rejection (§5.2) ──
  | 'unknown_metric'
  | 'missing_date'
  | 'invalid_date'
  | 'unknown_source';

const ORG_REASONS: ReadonlySet<string> = new Set<OrgReason>([
  'not_yours',
  'not_self_report',
  'challenge_ended',
  'future_date',
  'outside_window',
  'too_old',
  'not_joined',
  'invalid_value',
  'above_max',
  'proxy_stage',
  'no_slots',
  'slot_not_found',
  'date_not_offered',
  'already_booked',
  'guardian_missing',
  'slot_full',
  'not_a_member',
  'not_found',
  'unknown_metric',
  'missing_date',
  'invalid_date',
  'unknown_source',
]);

/** Narrow an arbitrary `data.reason` to a known reason; unknown/absent → null. */
export function toOrgReason(value: unknown): OrgReason | null {
  return typeof value === 'string' && ORG_REASONS.has(value) ? (value as OrgReason) : null;
}

/**
 * Per-reason copy. Preferred over the server `message` ONLY where the server's
 * is unusable (403 — clobbered) or absent; on 409/422 the server's Arabic
 * sentence is intact and ready to render, so {@link OrgErrorMapper} prefers it.
 */
export const ORG_REASON_KEYS: Readonly<Record<OrgReason, string>> = {
  not_yours: 'reference_portal_org_reason_not_yours',
  not_self_report: 'reference_portal_org_reason_not_self_report',
  challenge_ended: 'reference_portal_org_reason_challenge_ended',
  future_date: 'reference_portal_org_reason_future_date',
  outside_window: 'reference_portal_org_reason_outside_window',
  too_old: 'reference_portal_org_reason_too_old',
  not_joined: 'reference_portal_org_reason_not_joined',
  invalid_value: 'reference_portal_org_reason_invalid_value',
  above_max: 'reference_portal_org_reason_above_max',
  proxy_stage: 'reference_portal_org_reason_proxy_stage',
  no_slots: 'reference_portal_org_reason_no_slots',
  slot_not_found: 'reference_portal_org_reason_slot_not_found',
  date_not_offered: 'reference_portal_org_reason_date_not_offered',
  already_booked: 'reference_portal_org_reason_already_booked',
  guardian_missing: 'reference_portal_org_reason_guardian_missing',
  slot_full: 'reference_portal_org_reason_slot_full',
  not_a_member: 'reference_portal_org_reason_not_a_member',
  not_found: 'reference_portal_org_reason_not_found',
  unknown_metric: 'reference_portal_org_reason_unknown_metric',
  missing_date: 'reference_portal_org_reason_missing_date',
  invalid_date: 'reference_portal_org_reason_invalid_date',
  unknown_source: 'reference_portal_org_reason_unknown_source',
};

/**
 * The localized per-status fallback, used when the API sent no usable message
 * (null / empty / not a string) — and ALWAYS for 403, whose message is a lie.
 */
export const ORG_STATUS_KEYS = {
  /** 0 — the request never reached the server (offline / DNS / CORS). */
  offline: 'reference_portal_org_err_offline',
  timeout: 'reference_portal_org_err_timeout',
  validation: 'reference_portal_org_err_400',
  session: 'reference_portal_org_err_401',
  forbidden: 'reference_portal_org_err_403',
  notFound: 'reference_portal_org_err_404',
  conflict: 'reference_portal_org_err_409',
  invalid: 'reference_portal_org_err_422',
  rateLimited: 'reference_portal_org_err_429',
  server: 'reference_portal_org_err_500',
  generic: 'reference_portal_org_err_generic',
} as const;

/**
 * HTTP status → the i18n key for our own copy. `timedOut` distinguishes the two
 * status-0 cases the transport collapses together (rxjs `timeout()` vs. a real
 * network failure) — both leave the UI recoverable, but they say different things.
 */
export function orgMessageKeyFor(status: number, timedOut = false): string {
  if (!status) return timedOut ? ORG_STATUS_KEYS.timeout : ORG_STATUS_KEYS.offline;
  if (status === 400) return ORG_STATUS_KEYS.validation;
  if (status === 401) return ORG_STATUS_KEYS.session;
  if (status === 403) return ORG_STATUS_KEYS.forbidden;
  if (status === 404) return ORG_STATUS_KEYS.notFound;
  if (status === 409) return ORG_STATUS_KEYS.conflict;
  if (status === 422) return ORG_STATUS_KEYS.invalid;
  if (status === 429) return ORG_STATUS_KEYS.rateLimited;
  if (status >= 500) return ORG_STATUS_KEYS.server;
  return ORG_STATUS_KEYS.generic;
}

/**
 * Is the server's own `message` safe to render for this status?
 *
 * 401, 404 (with a message), 409 and 422 all keep what the controller sent and
 * are ready to show. ONLY 403 is lost — and 5xx/0 bodies are technical, never
 * user-facing. This is the single predicate the whole app asks before echoing a
 * B2B error back to a member.
 */
export function orgMessageIsTrustworthy(status: number): boolean {
  if (status === 403) return false; // §1.3 — clobbered, always
  if (!status || status >= 500) return false; // network / server internals
  return true;
}

/** `slot_full` is a normal outcome (trap 13) — thirty students share one break. */
export function isRetryableWithAnotherSlot(reason: OrgReason | null): boolean {
  return reason === 'slot_full';
}

/**
 * The normalized failure every org facade stores and every org view renders.
 * Always display-ready: `message` is localized prose, never a raw key, never a
 * stringified object, and never the clobbered 403 sentence.
 */
export interface OrgError {
  /** 0 for network/timeout. */
  status: number;
  reason: OrgReason | null;
  /** Localized, display-ready. */
  message: string;
  /** Laravel 422 `{ errors }`, bound to their inputs. Empty for every other failure. */
  fieldErrors: Readonly<Record<string, readonly string[]>>;
  /** `data.max` on an `above_max` refusal — the cap to show (§4.4). */
  max: number | null;
  /** `data.retry_after` on a handler 429 (§1.2). */
  retryAfter: number | null;
}

/** An empty {@link OrgError} shape — handy as a "no error" sentinel in tests. */
export const NO_ORG_ERROR: OrgError = {
  status: 0,
  reason: null,
  message: '',
  fieldErrors: {},
  max: null,
  retryAfter: null,
};
